import { toast } from "sonner";

// OpenRoute API key
const API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
const MODEL = "google/gemini-2.5-flash-preview-05-20";

export interface QuizQuestion {
  id: number;
  content: string;
  options: { id: string; text: string }[];
  correctAnswer: string;
  tactic: string;
  explanation: string;
}

export async function generateMisinformationQuestions(numQuestions: number = 5): Promise<QuizQuestion[]> {
  try {
    const prompt = `Generate ${numQuestions} example questions for a media literacy quiz that tests the user's ability to identify misinformation tactics.

For each question:
1. Provide a realistic example of content that might appear in social media or news that uses a specific misinformation tactic
2. Include 3 multiple choice options (labeled a, b, c) where only one correctly identifies the misinformation tactic
3. Indicate which option is correct
4. Name the specific misinformation tactic being used
5. Include a brief explanation of why this is an example of that tactic and what signs to look for

Format your response as a JSON array with this structure:
[
  {
    "id": 1,
    "content": "Example content showing misinformation",
    "options": [
      {"id": "a", "text": "Option description"},
      {"id": "b", "text": "Option description"},
      {"id": "c", "text": "Option description"}
    ],
    "correctAnswer": "b",
    "tactic": "Name of tactic",
    "explanation": "Explanation of the tactic and why this is an example"
  },
  ...
]`;

    // Send request to OpenRoute API
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_KEY}`,
        "HTTP-Referer": window.location.origin,
        "X-Title": "Truth Nexus AI"
      },
      body: JSON.stringify({
        model: MODEL,
        messages: [
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 1000
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("API Error:", errorText);
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Parse the response
    try {
      // The AI response is wrapped in a content field that we need to parse as JSON
      let rawResponseContent = data.choices[0].message.content;
      
      // Clean the response string: remove markdown code block fences if present
      if (rawResponseContent.startsWith("```json\n")) {
        rawResponseContent = rawResponseContent.substring(7, rawResponseContent.length - 3);
      } else if (rawResponseContent.startsWith("```")) {
        rawResponseContent = rawResponseContent.substring(3, rawResponseContent.length - 3);
      }

      console.log('Attempting to parse quiz questions JSON:', rawResponseContent); // Debugging line
      const aiResponse = JSON.parse(rawResponseContent);
      
      // Make sure we have an array of questions
      if (Array.isArray(aiResponse)) {
        return aiResponse as QuizQuestion[];
      } else if (Array.isArray(aiResponse.questions)) { // Sometimes the AI might wrap it in a 'questions' field
        return aiResponse.questions as QuizQuestion[];
      } else {
        // If the structure is unexpected, throw an error
        console.error("Unexpected AI response structure after parsing:", aiResponse);
        throw new Error("Unexpected response format from AI");
      }
    } catch (parseError) {
      console.error("Error parsing AI response:", parseError);
      throw new Error("Failed to parse quiz questions");
    }
  } catch (error) {
    console.error("Error generating questions:", error);
    toast.error("Failed to generate quiz questions. Using default questions instead.");
    
    // Return default questions as fallback
    return defaultQuizQuestions;
  }
}

// Default quiz questions to use as fallback
const defaultQuizQuestions: QuizQuestion[] = [
  {
    id: 1,
    content: "A popular news website reports: \"Scientists discover that coffee cures cancer! A new study shows drinking 3 cups daily eliminates cancer risk by 95%!\"",
    options: [
      { id: "a", text: "This is accurate reporting of scientific research" },
      { id: "b", text: "This uses sensational language and overstates research findings" },
      { id: "c", text: "This is properly contextualizing scientific research" },
    ],
    correctAnswer: "b",
    tactic: "Sensationalism",
    explanation: "This headline uses sensational language and makes an extreme claim. No legitimate scientific study would claim a 95% elimination of cancer risk from coffee consumption. Real scientific reporting would include qualifiers, limitations, and context about the specific research."
  },
  {
    id: 2,
    content: "Breaking: \"Anonymous source reveals government conspiracy to control weather using secret technology\"",
    options: [
      { id: "a", text: "This is factual news with verified sources" },
      { id: "b", text: "This is balanced reporting with multiple perspectives" },
      { id: "c", text: "This relies on anonymous sources making extraordinary claims without evidence" },
    ],
    correctAnswer: "c",
    tactic: "Unverified Sources",
    explanation: "This claim relies solely on an anonymous source making an extraordinary claim about weather control technology. Extraordinary claims require extraordinary evidence, and reputable journalism would require multiple sources and verification for such claims."
  },
  {
    id: 3,
    content: "POLL RESULTS: 95% of Americans support this controversial new policy! [Based on Twitter poll with 230 respondents]",
    options: [
      { id: "a", text: "This accurately represents public opinion with valid methodology" },
      { id: "b", text: "This misrepresents a limited sample as representing all Americans" },
      { id: "c", text: "This is proper statistical reporting" },
    ],
    correctAnswer: "b",
    tactic: "Statistical Manipulation",
    explanation: "This headline misrepresents a small, self-selected Twitter poll as representing all Americans' opinions. Legitimate polls require proper sampling methods, demographic balancing, and much larger sample sizes to make claims about nationwide opinions."
  },
  {
    id: 4,
    content: "An image showing a politician at what appears to be a protest, with the caption: \"Politician X supports violent protesters!\" [The image is from 10 years ago at an unrelated charity event]",
    options: [
      { id: "a", text: "This is accurate photo journalism" },
      { id: "b", text: "This uses an image out of context to create a false narrative" },
      { id: "c", text: "This is proper use of archival imagery" },
    ],
    correctAnswer: "b",
    tactic: "False Context",
    explanation: "This example takes an image from one context (a charity event) and presents it with false context (a protest) to create a misleading narrative. This is a common form of misinformation that misrepresents what's actually shown in an image."
  },
  {
    id: 5,
    content: "Research proves that Product X causes severe health issues! [Article written by Company Y that sells a competing product]",
    options: [
      { id: "a", text: "This is independent scientific research" },
      { id: "b", text: "This properly discloses conflicts of interest" },
      { id: "c", text: "This has an undisclosed conflict of interest that affects credibility" },
    ],
    correctAnswer: "c",
    tactic: "Hidden Conflict of Interest",
    explanation: "This content fails to disclose a significant conflict of interest—it's written by a competing company with financial incentive to discredit Product X. Credible reporting would prominently disclose such conflicts and rely on independent research."
  }
];
